import React, {
    cloneElement,
    useEffect,
    useState,
    createRef,
    useMemo,
    useRef
} from 'react';
import PropTypes from 'prop-types';

import { useIntl } from 'react-intl';

import { Badge, Form, InputGroup } from 'react-bootstrap';
import { Tooltip, Checkbox } from '@sumologic/yama-ds-components';

import DOMPurify from 'dompurify';

import {
    CALC,
    CHECKBOX,
    COLOR,
    DATE,
    DYNAMIC,
    LIST,
    MULTILIST,
    TAGS,
    TEXTAREA,
    TIME,
    TIMEINTERVAL,
    TIMEZONE
} from '~/modules/customFieldTypes';

import { stripHtml } from '~/modules/helpers';

import {
    formatAsUserLongDate,
    formatAsUserLongDateTime,
    getDateUnitTranslationObj
} from '~/modules/helpers/date';
import { isTrue } from '~/modules/helpers/boolean';
import { checkOverflow } from '~/modules/helpers/element';
import { isFalsyOrIsEmptyObject } from '~/modules/helpers/object';

import Icon from '~/components/Icon/Icon';
import Translate from '~/components/Translate/Translate';
import PortalDatePicker from '~/components/DatePicker/PortalDatePicker';
import useResizeObserver from '~/components/useResizeObserver/useResizeObserver';
import useResizeObserverRef from '~/components/useResizeObserver/useResizeObserverRef';

const Editable = ({
    children,
    className,
    truncateText = true,
    rawComponent,
    notEditable,
    isEditable,
    setEditable,
    keyId,
    type,
    id
}) => {
    const canEnableEditable = !notEditable;

    const enableEditable = () => {
        if (canEnableEditable || !truncateText) {
            setEditable(id, true);
        }
    };

    const disableEditable = () => {
        setEditable(id, false);
    };

    // todo: revise refs and observers

    const cloneRef = createRef();
    const ellipsisRef = useRef();

    const [stateEllipsisRef, setStateEllipsisRef] = useState(false);

    const [resizeRef, resizeEntry] = useResizeObserver();
    const [setObservedNode] = useResizeObserverRef();

    useEffect(() => {
        const el = ellipsisRef.current;
        if (el) {
            setObservedNode(el);
            setStateEllipsisRef(ellipsisRef);
        }
    }, [ellipsisRef]);

    const canEdit =
        CHECKBOX === type || COLOR === type ? truncateText : isEditable;

    useEffect(() => {
        if (canEdit) {
            const cloned = cloneRef.current;
            if (cloned) {
                if ('checkbox' !== cloned.type) {
                    // mm: standard input focus
                    cloned.focus?.();
                } else {
                    const inputEl = cloned.input;
                    if (inputEl && !cloned.isCalendarOpen()) {
                        // mm: DatePicker custom input focus
                        inputEl.focus?.();
                    }
                }
            }
        }
    }, [canEdit, cloneRef]);

    const cloneChildren = params => {
        return cloneElement(children, {
            ...params,
            ref: cloneRef
        });
    };

    const childProps = children.props;

    const defaultEditable = () => {
        const stopEdit = arr => {
            childProps.onBlur?.(...arr);
            disableEditable();
        };
        return cloneChildren({
            onBlur: (...args) => {
                stopEdit(args);
            },
            onKeyDown: (...args) => {
                childProps.onKeyDown?.(...args);
                if ('Enter' === args[0].key) {
                    stopEdit(args);
                }
            }
        });
    };

    const editableClasses = `editable ${className || 'standard'} ${
        notEditable
            ? 'not' // 𝘊𝘰𝘯𝘴𝘦𝘲𝘶𝘦𝘯𝘵𝘭𝘺 𝘩𝘰𝘷𝘦𝘳 𝘦𝘧𝘧𝘦𝘤𝘵 𝘪𝘴 𝘳𝘦𝘮𝘰𝘷𝘦𝘥
            : 'is'
    }-clickable`;

    const withEllips = (value, control) => {
        return value && checkOverflow(stateEllipsisRef) ? (
            <Tooltip title={value} placement="top" arrow>
                {control}
            </Tooltip>
        ) : (
            control
        );
    };

    const notEditableTextControl = value => {
        const control = (
            <Form.Control
                readOnly
                plaintext
                ref={ellipsisRef}
                className="text-truncate"
                value={value}
            />
        );
        return (
            <span className={editableClasses}>
                {withEllips(value, control)}
            </span>
        );
    };

    const notEditableTextControlFake = value => {
        return (
            <span className="form-control-plaintext text-break pt-0">
                {value}
            </span>
        );
    };

    switch (type) {
        //# 1, 6, 8, 12
        case LIST:
        case TIMEZONE:
        case DYNAMIC:
        case MULTILIST:
            if (canEdit) {
                return cloneChildren({
                    menuIsOpen: true,
                    onBlur: disableEditable,
                    onChange: childProps.onChange,
                    className: [
                        ...(childProps.className || []),
                        'editable-list'
                    ]
                });
            } else {
                let value;
                const rawValue = childProps.value;
                if (rawValue) {
                    if (rawValue.map && Array.isArray(rawValue)) {
                        value = rawValue.map(e => e.label).join(',\u0020');
                    } else if (rawValue.hasOwnProperty('label')) {
                        value = rawValue.label;
                    }
                }
                value ??= '';
                // console.log({ rawValue, value });
                if (truncateText) {
                    const control = (
                        <Form.Control
                            readOnly
                            plaintext
                            ref={ellipsisRef}
                            className="text-truncate"
                            onClick={enableEditable}
                            onFocus={enableEditable}
                            value={value}
                        />
                    );
                    return (
                        <span className={editableClasses}>
                            <InputGroup>
                                {'mssp' === rawValue?.type && (
                                    <div className="mssp-icon input-group-prepend d-flex">
                                        <InputGroup.Text>
                                            <Icon type="group-work" />
                                        </InputGroup.Text>
                                    </div>
                                )}
                                {withEllips(value, control)}
                            </InputGroup>
                        </span>
                    );
                } // else:
                return notEditableTextControlFake(value);
            }
        //# 3, 4
        case DATE:
        case TIME:
            if (canEdit) {
                return cloneChildren({
                    onChange: childProps.onChange,
                    onClickOutside: disableEditable,
                    onSelect: disableEditable
                });
            } else {
                const rawValue = childProps.value;
                const value = rawValue
                    ? (TIME !== type
                          ? formatAsUserLongDate
                          : formatAsUserLongDateTime)(rawValue)
                    : '';

                if (truncateText) {
                    const controlClasses = ['text-truncate'];
                    if (childProps.isInvalid) {
                        controlClasses.push('is-invalid');
                    }
                    const control = (
                        <Form.Control
                            plaintext
                            readOnly
                            className={controlClasses.join('\u0020')}
                            onClick={enableEditable}
                            onFocus={enableEditable}
                            value={value}
                            ref={ellipsisRef}
                        />
                    );
                    return (
                        <span className={editableClasses} key={keyId}>
                            {notEditable ? (
                                withEllips(value, control)
                            ) : (
                                <PortalDatePicker {...childProps} />
                            )}
                        </span>
                    );
                } // else:
                return notEditableTextControlFake(value);
            }
        //# 21
        case TIMEINTERVAL:
            if (canEdit) {
                return defaultEditable();
            } else {
                let value = '';
                const rawValue = childProps.field.value;
                if (rawValue) {
                    const { unit, value: amount } = rawValue;
                    if ('' !== unit && '' !== amount) {
                        const intl = useIntl();
                        value = `${amount} ${intl.formatMessage(
                            getDateUnitTranslationObj(unit)
                        )}`;
                    }
                }
                // console.log({ rawValue, value });
                if (truncateText) {
                    return (
                        <span className={editableClasses} key={keyId}>
                            <Form.Control
                                plaintext
                                readOnly
                                className="text-truncate"
                                onClick={enableEditable}
                                onFocus={enableEditable}
                                value={value}
                            />
                        </span>
                    );
                } // else:
                return notEditableTextControlFake(value);
            }
        //# 22
        case TEXTAREA:
            if (canEdit) {
                return defaultEditable();
            } // else:
            const rawValue = childProps.value;
            const value = rawValue
                ? useMemo(() => {
                      const plainText = stripHtml(rawValue);
                      return DOMPurify.sanitize(plainText);
                  }, [rawValue])
                : '';
            // console.log({ rawValue, value });
            return (
                <span
                    ref={resizeRef}
                    className="form-control-plaintext text-break pt-0 h-100 editable"
                    onClick={enableEditable}
                    onFocus={enableEditable}
                    dangerouslySetInnerHTML={{
                        __html: value
                    }}
                />
            );
        //# 2
        case CHECKBOX:
            if (canEdit) {
                return cloneChildren({
                    onChange: childProps.onChange
                });
            } else {
                const rawValue = childProps.value;
                const value = rawValue ? isTrue(rawValue) : false;
                // console.log({ rawValue, value });
                return (
                    <span className={`inline ${editableClasses}`} key={keyId}>
                        <div
                            className="editable-checkbox-container"
                            onClick={enableEditable}
                            onFocus={enableEditable}
                        >
                            <Checkbox disabled={true} checked={value} />
                        </div>
                    </span>
                );
            }
        //# 10
        case COLOR:
            if (canEdit) {
                return cloneChildren({
                    onChange: (...args) => {
                        childProps.onChange?.(...args);
                        const color = args[0].rgb;
                        const colorValues = Object.values(color);
                        // console.log({ args, color, colorValues });
                        colorValues.pop();
                        if (!colorValues.every(e => 0 === e)) {
                            disableEditable();
                        }
                    }
                });
            } else {
                const kind = typeof childProps.title;
                // prettier-ignore
                const rawValue =
                    'string' === kind
                        ? childProps.value
                        : 'undefined' !== kind
                            ? childProps.title
                            : null;
                // prettier-ignore
                const value = rawValue ? `rgb(${rawValue.r},${rawValue.g},${rawValue.b})` : '';
                // console.log({ rawValue, value });
                return (
                    <span className={`inline ${editableClasses}`} key={keyId}>
                        <div
                            className="color-picker text-truncate"
                            onClick={enableEditable}
                            onFocus={enableEditable}
                        >
                            <Icon type="circle2" style={{ color: value }} />
                        </div>
                    </span>
                );
            }
        //# 7
        case TAGS:
            if (canEdit) {
                return cloneChildren({
                    onChange: childProps.onChange,
                    onBlur: disableEditable
                });
            } else {
                const labels = [];
                const badges = [];
                const rawValue = childProps.value;
                if (rawValue?.length) {
                    for (const item of rawValue) {
                        const str = item.label;
                        if (str && !labels.includes(str)) {
                            const index = labels.push(str);
                            badges.push(
                                <Badge
                                    key={index}
                                    bg="primary"
                                    className="me-1"
                                >
                                    {str}
                                </Badge>
                            );
                        }
                    }
                }
                if (badges.length > 0 || canEnableEditable) {
                    const handler = canEnableEditable ? enableEditable : null;
                    const hasOverflow = checkOverflow(stateEllipsisRef);
                    return (
                        <span className={`tags ${editableClasses}`} key={keyId}>
                            <div
                                className={
                                    truncateText ? 'text-truncate' : null
                                }
                                onClick={handler}
                                onFocus={handler}
                                ref={ellipsisRef}
                            >
                                {hasOverflow ? (
                                    <Tooltip
                                        title={labels.join(',\u0020')}
                                        placement="left"
                                        arrow
                                    >
                                        <div className="d-unset">{badges}</div>
                                    </Tooltip>
                                ) : (
                                    badges
                                )}
                                {canEnableEditable && !hasOverflow && (
                                    <Badge
                                        bg=""
                                        key="empty-tag"
                                        className="empty-tag me-1"
                                    >
                                        <Translate message="Add new" />
                                        <Icon
                                            size="sm"
                                            type="plus"
                                            className="ms-1"
                                        />
                                    </Badge>
                                )}
                            </div>
                        </span>
                    );
                } // else:
                return null;
            }
        //# 14
        case CALC:
            if (canEdit) {
                return defaultEditable();
            } else {
                return (
                    truncateText
                        ? notEditableTextControl
                        : notEditableTextControlFake
                )(childProps.value);
            }
        //#
        default:
            if (canEdit) {
                return defaultEditable();
            } else {
                const rawValue = childProps.value;
                const showBlank =
                    0 !== rawValue && isFalsyOrIsEmptyObject(rawValue);
                const value = showBlank ? '' : rawValue;
                // console.log({ rawValue, value });
                if (truncateText) {
                    const baseProps = {
                        className: 'text-truncate',
                        onClick: enableEditable,
                        onFocus: enableEditable,
                        ref: ellipsisRef
                    };
                    const control = rawComponent ? (
                        <span {...baseProps}>{value}</span>
                    ) : (
                        <Form.Control
                            {...baseProps}
                            readOnly
                            plaintext
                            value={value}
                        />
                    );
                    // todo: handle showBlank and revise tooltip-bottom
                    const observedEl = resizeEntry.target;
                    return (
                        <>
                            <span key={keyId} className={editableClasses}>
                                {withEllips(value, control)}
                            </span>
                            <Tooltip
                                title={<Translate message={value} />}
                                placement="bottom"
                                arrow
                                key={`overlay-trigger ${keyId || 'keyId'}`}
                            >
                                <div
                                    className={
                                        observedEl && checkOverflow(observedEl)
                                            ? 'ellipsis'
                                            : 'ellipsis-hidden'
                                    }
                                />
                            </Tooltip>
                        </>
                    );
                } else {
                    return notEditableTextControlFake(value);
                }
            }
    }
};

Editable.propTypes = {
    children: PropTypes.element.isRequired,
    setEditable: PropTypes.func.isRequired,
    isEditable: PropTypes.bool.isRequired,
    notEditable: PropTypes.bool,
    truncateText: PropTypes.bool,
    type: PropTypes.number
};

// Editable.defaultProps = {};

export default Editable;
